<template>
    <article>
        <p>
            After installation, Coroot will prompt you to set a password for the admin user:
        </p>

        <v-card max-width="1200">
            <v-img eager src="/static/img/docs/coroot/admin_password.png" />
        </v-card>

        <p>
            To prevent someone else from setting the admin password before you, you can specify the initial password
            using the <var>--auth-bootstrap-admin-password</var> CLI argument or the <var>AUTH_BOOTSTRAP_ADMIN_PASSWORD</var> environment variable.
            This initial password can be changed later through the UI.
        </p>

        <h2>Anonymous mode</h2>

        <p>
            To disable authentication, use the <var>--auth-anonymous-role</var> CLI argument or the <var>AUTH_ANONYMOUS_ROLE</var> environment variable,
            setting it to one of the following roles: Admin, Editor, or Viewer.
        </p>

        <h2>Reset admin password</h2>

        <p>
            To reset admin password, use the following command:
        </p>

        <pre v-hl.bash>
$ coroot set-admin-password
Enter new password:
Confirm new password:
Admin password set successfully.
        </pre>

        <h2>Manage users</h2>

        <p>
            To manage Coroot users, go to the project settings (<v-icon small>mdi-cog</v-icon>),
            click on Organization:
        </p>

        <v-card max-width="1200">
            <v-img eager src="/static/img/docs/coroot/users.png" />
        </v-card>

        <p>
            To add a new user, click "Add user", fill out the form, and select a role.
        </p>

        <v-card max-width="600">
            <v-img eager src="/static/img/docs/coroot/add_user.png" />
        </v-card>

        <p>
            The Coroot Community Edition includes three predefined roles: Admin, Editor, and Viewer.
            The Enterprise Edition allows you to create custom roles with with granular permissions.
        </p>

        <h2>Single Sign-On (SSO)</h2>

        <v-alert color="info" outlined text class="mt-5">
            Single Sign-On is available only in Coroot Enterprise (from $1 per CPU core/month).
            <a href="https://coroot.com/account" target="_blank" class="font-weight-bold">Start</a> your free trial today.
        </v-alert>

        <p>
            Single Sign-On (SSO) feature streamlines user authentication by allowing team members to access the Coroot
            platform using a single set of credentials linked to an identity provider, such as Google Workspace, Okta, or other SSO solutions.
            With SSO, users no longer need to manage separate passwords for Coroot, enhancing both security and user experience.
        </p>

        <p>
            Coroot's Single Sign-On (SSO) uses the SAML protocol, where Coroot acts as the service provider (SP).
            SAML allows users to log in through an identity provider (IdP) and access Coroot without needing separate credentials.
            This makes the login process easier and more secure by centralizing authentication through the IdP.
        </p>

        <h3>Setup SAML with Okta</h3>
        <ul>
            <li>Log in to the <a href="https://login.okta.com/" target="_blank">Okta portal</a>.</li>
            <li>Go to the Admin Console in your Okta organization.</li>
            <li>Navigate to <b>Applications</b> > <b>Applications</b>.</li>
            <li>Click <b>Create App Integration</b>.</li>
            <li>Select <b>SAML 2.0</b> as the <b>Sign-in method</b>.</li>
            <li>Click <b>Next</b>.</li>
            <li>
                On the <b>General Settings</b> tab, enter a name for your Coroot integration.
                You can also upload the <a href="https://coroot.com/static/img/coroot_512.png" target="_blank">logo</a>.
                <v-card max-width="600">
                    <v-img eager src="/static/img/docs/coroot/saml_okta_app.png" />
                </v-card>
            </li>
            <li>
                On the <b>Configure SAML</b> tab:
                <ul>
                    <li>
                        For both <b>Single sign on URL</b> and <b>Audience URI (SP Entity ID)</b> fields use the
                        <var>https://&lt;COROOT_ADDRESS&gt;/sso/saml</var> URL.
                        <v-card max-width="600">
                            <v-img eager src="/static/img/docs/coroot/saml_okta_params.png" />
                        </v-card>
                    </li>
                    <li>
                        In the <b>Attribute Statements</b> section, configure <var>Email</var>, <var>FirstName</var>, and <var>LastName</var> attributes.
                        <v-card max-width="600">
                            <v-img eager src="/static/img/docs/coroot/saml_okta_attributes.png" />
                        </v-card>
                    </li>
                </ul>
            </li>
            <li>Click <b>Next</b>.</li>
            <li>On the final <b>Feedback</b> tab, fill out the form and then click <b>Finish</b>.</li>
            <li>
                Download <var>Identity Provider Metadata XML</var> using the <b>Metadata URL</b>:
                <v-card max-width="600">
                    <v-img eager src="/static/img/docs/coroot/saml_okta_metadata.png" />
                </v-card>
            </li>
        </ul>

        <h3>Configure SAML authentication in Coroot</h3>
        <ul>
            <li>
                Navigate to the <b>Project Settings (<v-icon small>mdi-cog</v-icon>)</b> > <b>Organization</b> > <b>Single Sign-On (SAML)</b> section.
                <v-card max-width="800">
                    <v-img eager src="/static/img/docs/coroot/saml_upload_metadata.png" />
                </v-card>
            </li>
            <li>
                Use the <b>Upload Identity Provider Metadata XML</b> button to upload the IDP metadata file that was previously downloaded.
            </li>
            <li>Click <b>Save and Enable</b>.</li>
        </ul>
        <v-card max-width="800">
            <v-img eager src="/static/img/docs/coroot/saml_enabled.png" />
        </v-card>
        <p>
            Once Single Sign-On is enabled, Coroot will redirect your team members to the Identity Provider for authentication.
        </p>
        <p>
            Each team member authenticated through the Identity Provider will be displayed in the Users list in Coroot,
            allowing you to manually change their roles.
        </p>
        <p>
            Use <var>http://&lt;COROOT_ADDRESS&gt;/login</var> page and the <b>admin</b> user credentials
            to log in to your Coroot instance if you encounter any issues with SSO.
        </p>
    </article>
</template>

<script>

export default {
}
</script>

<style scoped>
b {
    font-weight: 500;
}
</style>
